import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import './Login.css';
import '../forms.css';
import { ReactComponent as MntLogo } from '../assets/svg/cimco-mnt-logo.svg';
import { LoadingContext } from '../context/LoadingContext';
import bcrypt from 'bcryptjs';
import Spinner from './Spinner';

const QrLogin = ({ setAuthToken }) => {
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [username, setUsername] = useState('');
  // Account password field (used in both login and registration)
  const [password, setPassword] = useState('');
  // Registration-specific field for the registration code
  const [registrationCode, setRegistrationCode] = useState('');
  // Additional fields for registration
  const [userfirstname, setUserfirstname] = useState('');
  const [userlastname, setUserlastname] = useState('');
  const [useremail, setUseremail] = useState('');
  const [userMobile, setUserMobile] = useState('');
  // New state for the role and rate per hour
  const [userRole, setUserRole] = useState("internal technician");
  const [ratePerHour, setRatePerHour] = useState("");
  const [company, setCompany] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyContactPerson, setCompanyContactPerson] = useState('');
  const [companyContactNumber, setCompanyContactNumber] = useState('');
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const machineId = searchParams.get('machineId'); // Extract machineId from URL
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t } = useTranslation();

  const [settings, setSettings] = useState({});
  useEffect(() => {
    axios.get(`${apiBaseUrl}/mnt/api/settings`)
      .then(res => setSettings(res.data))
      .catch(err => console.error('Error loading settings:', err));
  }, [apiBaseUrl]);

  

  // Handler for login
  const handleLogin = async (e) => {
    e.preventDefault();
    console.log("Login triggered by:", e.nativeEvent.submitter);
    setIsLoading(true);
    try {
const response = await axios.post(`${apiBaseUrl}/mnt/api/login?machineId=${machineId}`, {
  username,
  password,
});

      const { token } = response.data;
      setAuthToken(token);
      localStorage.setItem('token', token);

      let redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (!redirectUrl) {
        const params = new URLSearchParams(window.location.search);
        redirectUrl = params.get('redirect') ? decodeURIComponent(params.get('redirect')) : null;
      }
      console.log(`Redirecting QR login user to: ${redirectUrl}`);
      sessionStorage.removeItem('redirectAfterLogin');
      window.location.reload();
      // Optionally, navigate here: navigate(redirectUrl || '/mnt/');
    } catch (error) {
      console.error('Login error:', error);
      setError(t('Login.InvalidCredentials'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for technician registration
  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Verify that the provided registration code is correct
      if (!settings.registrationCode || registrationCode !== settings.registrationCode) {
        setError(t("Login.InvalidRegistrationCode"));
        setIsLoading(false);
        return;
      }
      // Hash the user's chosen password
      const hashedPassword = await bcrypt.hash(password, 10);

      // Build the registration payload, including the chosen role and rate (if external)
      const payload = {
        username,
        password: hashedPassword,
        userfirstname,
        userlastname,
        userexternal: 0,
        usermobilenumber: userMobile,
        userskills: '',
        company: userRole === "external technician" ? company : '',
        companyaddress: userRole === "external technician" ? companyAddress : '',
        companycontactperson: userRole === "external technician" ? companyContactPerson : '',
        companycontactnumber: userRole === "external technician" ? companyContactNumber : '',
        rateperhour: userRole === "external technician" ? ratePerHour : 0,
        usernote: '',
        useremail,
        user_rights: userRole,
        receive_email: 0,
        machineIDs: machineId || ''
      };

      // Call the backend endpoint to register the technician
      const response = await axios.post(`${apiBaseUrl}/mnt/api/technician/add`, payload);
      console.log('Registration response:', response.data);
      setIsRegistering(false);
      setError('');
      alert(t("Login.RegistrationSuccess"));
    } catch (error) {
      console.error('Registration error:', error);
      setError(t("Login.RegistrationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login">
      {isLoading && <Spinner />}
      <div className="login-box">
        <div className="login-box__logo">
          <MntLogo />
        </div>
        <form onSubmit={isRegistering ? handleRegister : handleLogin}>
          <div className="entry-item entry-item__full-width">
            <label className="entry-label">{t('Login.UsernameLabel')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="form-login-control"
            />
          </div>
          {/* Always show the password field for account password */}
          <div className="entry-item entry-item__full-width">
            <label className="entry-label">{t('Login.PasswordLabel')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="form-login-control"
            />
          </div>

          {isRegistering && (
            <>
              <div className="entry-item entry-item__full-width">
                <label className="entry-label">{t('Login.RegistrationCode')}</label>
                <input
                  type="password"
                  value={registrationCode}
                  onChange={(e) => setRegistrationCode(e.target.value)}
                  required
                  className="form-login-control"
                />
              </div>
              <div className="entry-item entry-item__full-width">
                <label className="entry-label">{t('Login.FirstName')}</label>
                <input
                  type="text"
                  value={userfirstname}
                  onChange={(e) => setUserfirstname(e.target.value)}
                  required
                  className="form-login-control"
                />
              </div>
              <div className="entry-item entry-item__full-width">
                <label className="entry-label">{t('Login.LastName')}</label>
                <input
                  type="text"
                  value={userlastname}
                  onChange={(e) => setUserlastname(e.target.value)}
                  required
                  className="form-login-control"
                />
              </div>
              <div className="entry-item entry-item__full-width">
                <label className="entry-label">{t('Login.Email')}</label>
                <input
                  type="email"
                  value={useremail}
                  onChange={(e) => setUseremail(e.target.value)}
                  required
                  className="form-login-control"
                />
              </div>
              <div className="entry-item entry-item__full-width">
                <label className="entry-label">{t('Login.Mobile')}</label>
                <input
                  type="number"
                  value={userMobile}
                  onChange={(e) => setUserMobile(e.target.value)}
                  required
                  className="form-login-control"
                />
              </div>
              {/* Dropdown for selecting user role */}
              <div className="entry-item entry-item__full-width">
                <label className="entry-label">{t('Login.UserRole')}</label>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  required
                  className="form-login-control"
                >
                  <option value="internal technician">{t('Login.Roles.InternalTechnician')}</option>
                  <option value="external technician">{t('Login.Roles.ExternalTechnician')}</option>
                  <option value="operator">{t('Login.Roles.Operator')}</option>
                </select>
              </div>
              {/* If the user selects "external technician", show a field for rate per hour */}
              {userRole === "external technician" && (
                <>
                  <div className="entry-item entry-item__full-width">
                    <label className="entry-label">{t('Login.RatePerHour')}</label>
                    <input
                      type="number"
                      value={ratePerHour}
                      onChange={(e) => setRatePerHour(e.target.value)}
                      required
                      className="form-login-control"
                    />
                  </div>

                  <div className="entry-item entry-item__full-width">
                    <label className="entry-label">{t('Login.Company')}</label>
                    <input
                      type="text"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      required
                      className="form-login-control"
                    />
                  </div>

                  <div className="entry-item entry-item__full-width">
                    <label className="entry-label">{t('Login.CompanyAddress')}</label>
                    <input
                      type="text"
                      value={companyAddress}
                      onChange={(e) => setCompanyAddress(e.target.value)}
                      required
                      className="form-login-control"
                    />
                  </div>

                  <div className="entry-item entry-item__full-width">
                    <label className="entry-label">{t('Login.CompanyContactPerson')}</label>
                    <input
                      type="text"
                      value={companyContactPerson}
                      onChange={(e) => setCompanyContactPerson(e.target.value)}
                      required
                      className="form-login-control"
                    />
                  </div>

                  <div className="entry-item entry-item__full-width">
                    <label className="entry-label">{t('Login.CompanyContactNumber')}</label>
                    <input
                      type="text"
                      value={companyContactNumber}
                      onChange={(e) => setCompanyContactNumber(e.target.value)}
                      required
                      className="form-login-control"
                    />
                  </div>
                </>
              )}
            </>  
          )}

          {error && <div className="login-box__error-msg">{error}</div>}
          <button type="submit" className="button--full-width button--grey" 
          disabled={isLoading}
          >
            {isRegistering ? t('Login.RegisterButton') : t('Login.LoginButton')}
          </button>
          <button
  type="button"
  onClick={() => {
    setError('');
    setIsRegistering(!isRegistering);
  }}
  className="button--full-width button--grey"
  disabled={isLoading}
>
  {isRegistering ? t('Login.SwitchToLogin') : t('Login.RegisterButton')}
</button>

        </form>
      </div>
    </div>
  );
};

export default QrLogin;
